/**
 * Recovery for failed dynamic imports (`vite:preloadError` and React.lazy).
 *
 * A chunk request fails when the hashed file it points at is no longer on the CDN —
 * the tab is still running a previous build — or when the SPA fallback answers a
 * missing `/assets/*.js` with index.html, so the browser gets HTML where it expected
 * a module.
 *
 * Never call `event.preventDefault()` on `vite:preloadError`: vite's preload helper
 * rethrows only when the event was not cancelled, so suppressing it makes the import
 * resolve with `undefined`, and React.lazy then throws "can't access property
 * default, _result is undefined" — an unrecoverable render crash instead of a plain
 * chunk error the boundary can handle.
 */

const RETRIES_KEY = 'vite_preload_retries'
const LAST_RELOAD_KEY = 'vite_last_reload'

/** Reloads allowed per session before giving up and showing the error screen */
const MAX_RETRIES = 3
/** Minimum gap between reloads, so a permanently missing chunk cannot spin */
const COOL_DOWN_MS = 10 * 1000
/** How long the app must stay up before the retry budget is handed back */
const STABLE_SESSION_MS = 30 * 1000

const CHUNK_ERROR_PATTERNS = [
  /ChunkLoadError/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
  // MIME mismatch — index.html served in place of a deleted chunk
  /expected a javascript(-or-wasm)? module/i,
  /disallowed MIME type/i,
  // vite swallowed the preload error, so React.lazy resolved with `undefined`
  /_result is undefined/i,
  /reading '?default'?/i,
]

export const isChunkLoadError = (error: any): boolean => {
  if (!error) return false

  const text = `${error?.name || ''} ${error?.message || ''}`
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text))
}

/**
 * Reloads the page to pick up the current index.html, within a per-session budget.
 * Returns whether a reload was actually triggered.
 */
export const reloadForChunkError = (source: string): boolean => {
  const retries = Number(sessionStorage.getItem(RETRIES_KEY) || 0)
  const lastReload = Number(sessionStorage.getItem(LAST_RELOAD_KEY) || 0)
  const now = Date.now()

  if (retries >= MAX_RETRIES) {
    console.error(`[Chunk Reload] Preload failed too many times (${source}) — aborting reloads.`)
    return false
  }

  if (now - lastReload < COOL_DOWN_MS) {
    console.warn(`[Chunk Reload] Cool down active (${source}) — skipping reload.`)
    return false
  }

  sessionStorage.setItem(RETRIES_KEY, `${retries + 1}`)
  sessionStorage.setItem(LAST_RELOAD_KEY, `${now}`)

  console.warn(
    `[Chunk Reload] Chunk error detected (${source}) — retrying (${retries + 1}/${MAX_RETRIES})`
  )
  window.location.reload()
  return true
}

/**
 * Hands the retry budget back once the app has stayed up for a while. Clearing it on
 * every `load` made MAX_RETRIES unreachable, because each reload reset the counter.
 */
export const releaseChunkReloadBudgetWhenStable = () => {
  window.setTimeout(() => {
    sessionStorage.removeItem(RETRIES_KEY)
    sessionStorage.removeItem(LAST_RELOAD_KEY)
  }, STABLE_SESSION_MS)
}
